import { action, observable, reaction, runInAction } from "mobx";
import alertToast from 'Util/ToastifyUtils';
import * as uuid from 'uuid';
import { CreditProviderEndpoints } from '../endpoints/CreditProviderEndpoints';
import { ICreditProviderItemStore, ProviderTypeDto } from '../types/CreditProviderItemStoreType';
import type { CreditProviderDto } from '../types/CreditProviderListStoreTypes';

const defaultCreditProvider: CreditProviderDto = {
	id: "",
	name: "",
	description: "",
	isActive: false,
	providerTypeId: "",
	providerTypeName: "",
}

export class CreditProviderItemStore implements ICreditProviderItemStore {
	@observable creditProvider: CreditProviderDto;
	@observable isLoading: boolean = false;
	@observable hasError: boolean = false;
	@observable providerTypeList: ProviderTypeDto[] = [];

	constructor() {
		this.creditProvider = defaultCreditProvider;

		reaction(
			() => this.creditProvider.id,
			() => {
				if (!!this.creditProvider.id) {
					this.getCreditProvider(this.creditProvider.id);
				}
			}
		)
	}

	@action reset = () => {
		this.creditProvider = defaultCreditProvider;
		this.isLoading = false;
		this.hasError = false;
	}

	@action updateCreditProvider = (creditProvider: CreditProviderDto) => {
		this.creditProvider = creditProvider;
	}

	@action getCreditProvider = async (creditProviderId: string) => {
		this.isLoading = true;
		this.hasError = false;

		const result = await CreditProviderEndpoints.getCreditProviderDetail(creditProviderId);
		runInAction(() => {
			if (!result) {
				console.error("Error getting credit provider detail");
				this.hasError = true;
				this.isLoading = false;
				this.creditProvider = defaultCreditProvider;
				return;
			}
			this.isLoading = false;
			this.creditProvider = result;
		});
	}

	@action saveCreditProviderChanges = async (isNew: boolean = false) => {
		this.isLoading = true;
		this.hasError = false;

		// add new credit provider if isNew flag is set to true
		if (isNew) {
			const providerId = uuid.v4();
			const result = await CreditProviderEndpoints.addCreditProvider({...this.creditProvider, id: providerId});
			if (!result) {
				console.error("Error adding credit provider");
				this.hasError = true;
				this.isLoading = false;
				return;
			}
			runInAction(() => {
				this.isLoading = false;
				alertToast("Credit provider added successfully", "success");
			});
		} else {
			// update existing credit provider if isNew flag is set to false
			const result = await CreditProviderEndpoints.updateCreditProvider(this.creditProvider.id, this.creditProvider);
			runInAction(() => {
				if (!result) {
					console.error("Error updating credit provider");
					this.hasError = true;
					this.isLoading = false;
					alertToast("Error updating credit provider", "error");
					return;
				}
				this.isLoading = false;
				alertToast("Credit provider updated successfully", "success");
			});
		}
	}

	@action getProviderTypeList = async () => {
		this.isLoading = true;
		const result = await CreditProviderEndpoints.getProviderType();
		runInAction(() => {
			if (result.length === 0) {
				console.error("Unable to find any provider types.");
				alertToast("Unable to find any provider types.", "warning");
				this.isLoading = false;
				return;
			}
			this.isLoading = false;
			this.providerTypeList = result;
		});
	}
}